/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorComponentContainer" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { EndpointsEditorStore, IEndpointsEditorState } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { ServiceEndpoint, DataSourceDetails, ResultTransformationDetails, ServiceEndpointRequest, ServiceEndpointRequestResult, DataSource, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { IEndpointsEditorPanelCallback, IEndpointAuthSchemeSelectionPanelState, IEndpointTypesPanelState } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { EndpointsEditorPanelContentType, ActionsCreatorInstanceIds, TestConnectionDataSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { GenericFooterComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericFooterComponent";

import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ObservableValue } from "VSS/Core/Observable";
import { WrappedComponent } from "VSS/Platform/Layout";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { PerformanceConstants } from "DistributedTask/Common/SharedResources/Constants";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface IEndpointEditorComponentsContainerProps extends IEndpointsEditorPanelCallback {
    primaryOnClick: (serviceEndpoint?: ServiceEndpoint) => void;
    onDismiss: () => void;
    onRefresh: () => void;
    contentType: EndpointsEditorPanelContentType;
    sourceContentType: EndpointsEditorPanelContentType;
    endpointTypesPanelState?: IEndpointTypesPanelState;
    authSchemeSelectionPanelState?: IEndpointAuthSchemeSelectionPanelState;
    endpointType?: string;
    hideBackButton?: boolean;
    disableAllPipelinesSetting: boolean;
}

export class EndpointEditorComponentContainer extends VssComponent<IEndpointEditorComponentsContainerProps, IEndpointsEditorState> {
    constructor(props: IEndpointEditorComponentsContainerProps, context: IVssComponentContext) {
        super(props, context);

        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario(PerformanceConstants.EndpointEditorScenario, true);

        this._actionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._store = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this.state = this._store.getState();
        this._updateIsVerifiableConnection(this.state.serviceEndpointType, this.state.serviceEndpoint);
        this._store.addChangedListener(this._onStoreChange);
        this._isVerifying = false;
        this._invalidInputsCount = 0;
        this._loadingLabel = Resources.SavingSpinnerLabel;
        this._reloadFooter = new ObservableValue(false);
    }

    public componentWillUnmount() {
        this._store.removeChangedListener(this._onStoreChange);
    }

    public render(): React.ReactNode {
        if (!this.state.serviceEndpointType) {
            return (
                <Spinner
                    className={"service-endoint-type-spinner"}
                    label={""}
                    size={SpinnerSize.large}
                />
            );
        }

        let isAuthSchemeSupported = this._isAuthSchemeSupported();

        return (
            <div className="endpoint-editor-panel">
                {isAuthSchemeSupported && this._getEndpointEditorComponent()}
                {!this._hideFooter && this.state.isError && <MessageCard
                    className="flex-self-stretch  margin-top-16 margin-bottom-16"
                    severity={MessageCardSeverity.Error}
                   >
                   {this.state.errorMessage}
                   <br />
                   {this.state.activityId ? "ActivityId: " + this.state.activityId : null}
                </MessageCard>}
                {isAuthSchemeSupported && this._getFooterComponent()}
                {!isAuthSchemeSupported &&
                    <div className="connections-no-auth-scheme font-size-l">
                        {Utils_String.localeFormat(Resources.EndpointAuthorizationSchemeNotSupported, this.state.serviceEndpointType.displayName, this.state.serviceEndpoint.authorization.scheme)}
                    </div>
                }
            </div>
        );
    }

    public componentDidMount(): void {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.endScenario(PerformanceConstants.EndpointEditorFeature, PerformanceConstants.EndpointEditorScenario, true);
        if (this.props.disableAllPipelinesSetting) {
            this._actionCreator.updateOpenToAllPipelinesFlag(false);
        }
    }

    private _getEndpointEditorComponent(): React.ReactNode {
        let endpointEditorComponentProps: IEndpointEditorComponentProps = {
            draftEndpoint: this.state.serviceEndpoint,
            serviceEndpointType: this.state.serviceEndpointType,
            allowAllPipelinesFlagState: this.state.openToAllPipelinesFlag,
            disableAllPipelinesSetting: this.props.disableAllPipelinesSetting,
            isUpdateForm: this._isUpdateFlow(),
            updateDraftEndpointState: this._updateServiceEndpoint,
            updateInvalidInputsCount: this._handleUpdateInvalidInputsCount,
            updateAllowAllPipelinesFlag: this._updateOpenToAllPipelinesFlag
        };

        // Endpoint types with custom UI needs to have uiContributionId defined. If uiContributionId is not defined
        // for the endpoint type, use GenericEndpointEditorComponent (registered with 'endpoint-editor-component-generic').
        // The generic editor component generates endpoint editor input form based on input descriptors of that endpoint type.
        let uiContributionId = this.state.serviceEndpointType.uiContributionId ? this.state.serviceEndpointType.uiContributionId : "endpoint-editor-component-generic";

        return (
            this._isUpdateFlow() && !this.state.serviceEndpoint.id ?
                <div className="endpoint-editor-panel-content flex-column custom-scrollbar v-scroll-auto">
                    <Spinner
                        size={SpinnerSize.medium}
                        className="default-input-margin-top"
                    />
                </div>
                :
                <React.Fragment>
                    {/* Load endpoint editor component registered with the uiContributionId */}
                    <WrappedComponent
                        wrappedType={uiContributionId}
                        {...endpointEditorComponentProps}
                        isVerifiableConnection={this._verifiableConnection}
                        updateError={this._updateError}
                        getInvalidInputCount={this._getInvalidInputCount}
                        hidePanelFooter={this._hidePanelFooter}
                        closePanel={this.props.primaryOnClick}
                        onRefresh={this.props.onRefresh}
                        onBackButtonClick={this._onBackButtonClick}
                        hideBackButton={this.props.hideBackButton}
                        errorMessage= {this.state.errorMessage}
                    />
                </React.Fragment>
        );
    }

    private _getFooterComponent(): React.ReactNode {
        return (
            <Observer reloadFooter={this._reloadFooter}>
                {(props: { reloadFooter: boolean }) => {
                    return (
                        <React.Fragment>
                            {!this._hideFooter &&
                                <GenericFooterComponent
                                    isUpdateForm={this._isUpdateFlow()}
                                    enableSave={this._invalidInputsCount === 0}
                                    serviceEndpoint={this.state.serviceEndpoint}
                                    isVerifiable={this._verifiableConnection}
                                    showSaveButton={true}
                                    showBackButton={!this.props.hideBackButton}
                                    verifyInProgress={this._isVerifying}
                                    saveInProgress={this._isSaving}
                                    setupInProgress={this.state.setupInProgress}
                                    onVerifyAndSaveButtonClick={this._verifyAndSaveServiceEndpoint}
                                    onSaveButtonClick={this._saveServiceEndpoint}
                                    onBackButtonClick={this._onBackButtonClick} />
                            }
                        </React.Fragment>
                    );
                }}
            </Observer>
        );
    }

    private _getInvalidInputCount = (): Number => {
        return this._invalidInputsCount;
    }

    private _onStoreChange = (): void => {
        let state = this._store.getState();
        this._updateIsVerifiableConnection(this.state.serviceEndpointType, this.state.serviceEndpoint);
        this.setState(state);
    }

    private _updateIsVerifiableConnection(endpointType: ServiceEndpointType, endpoint: ServiceEndpoint): void {
        this._verifiableConnection = false;
        if (endpointType && endpointType.dataSources) {
            endpointType.dataSources.forEach((dataSource: DataSource) => {
                if (dataSource.name === TestConnectionDataSource
                    && !Utils_String.equals(endpoint.authorization.scheme, Constants.OAuthScheme, true)
                    && !Utils_String.equals(endpoint.authorization.scheme, Constants.OAuth2Scheme, true)) {
                    this._verifiableConnection = true;
                }
            });
        }

        if (endpointType && endpointType.authenticationSchemes && endpoint.authorization) {
            endpointType.authenticationSchemes.forEach(authScheme => {
                if (Utils_String.equals(endpoint.authorization.scheme, authScheme.scheme, true) && authScheme.properties) {
                    if (Utils_String.equals(authScheme.properties[Constants.isVerifiable], Boolean(false).toString(), true)) {
                        this._verifiableConnection = false;
                    } // isVerifiable true case is taken care of with availability of TestConnection DataSourceName
                }
            });
        }
    }

    private _hidePanelFooter = (hideFooter: boolean): void => {
        this._hideFooter = hideFooter;
        this._reloadFooter.value = !this._reloadFooter.value;
    }

    private _onBackButtonClick = () => {
        if (this._isUpdateFlow()) {
            this.props.onDismiss();
        } else {
            let contentType, panelTitle, panelDescription = null;
            let selectedEndpointType = this.state.serviceEndpointType;
            switch (this.props.sourceContentType) {
                case EndpointsEditorPanelContentType.ServiceEndpointTypes:
                    {
                        contentType = EndpointsEditorPanelContentType.ServiceEndpointTypes;
                        panelTitle = Resources.ChooseAzureRMSchemeTitle;
                    }
                    break;
                case EndpointsEditorPanelContentType.EndpointAuthSchemes:
                    {
                        contentType = EndpointsEditorPanelContentType.EndpointAuthSchemes;
                        const endpointType: string = this.props.endpointType ? this.props.endpointType : selectedEndpointType ? selectedEndpointType.name : null;
                        panelTitle = EndpointsEditorPanelUtils.getPageHeadingForAuthSchemeSelectionPanel(endpointType);
                        panelDescription = EndpointsEditorPanelUtils.getPageDescriptionForAuthSchemeSelectionPanel(endpointType);
                    }
                    break;
            }
            if (contentType !== null) {
                this.props.updateComponent({
                    contentType: contentType,
                    previousContentType: EndpointsEditorPanelContentType.CreateForm,
                    panelTitle: panelTitle,
                    endpointTypesPanelState: this.props.endpointTypesPanelState,
                    panelDescription: panelDescription,
                    authSchemeSelectionPanelState: this.props.authSchemeSelectionPanelState
                });
            }
        }
    }

    private _updateServiceEndpoint = (updatedEndpoint: ServiceEndpoint): void => {
        this._actionCreator.setServiceEndpoint(updatedEndpoint);
    }

    private _updateOpenToAllPipelinesFlag = (openToAllPipelinesFlag: boolean): void => {
        this._actionCreator.updateOpenToAllPipelinesFlag(openToAllPipelinesFlag);
    }

    private _handleUpdateInvalidInputsCount = (updateCountBy: number) => {
        this._invalidInputsCount += updateCountBy;
        this._reloadFooterBar();
    }

    private _saveServiceEndpoint = (): void => {
        this._changeIsSavingState(true);
        let updateEndpointPromise = this._isUpdateFlow() ?
            this._actionCreator.updateServiceEndpoint(this.state.serviceEndpoint) : this._actionCreator.saveServiceEndpoint(this.state.serviceEndpoint);

        updateEndpointPromise.then((serviceEndpoint: ServiceEndpoint) => {
            this._changeIsSavingState(false);
            this._actionCreator.saveResourceAuthorizationState(serviceEndpoint.id, this.state.openToAllPipelinesFlag);
            this.props.primaryOnClick(serviceEndpoint);
        }).catch((error) => {
            this._changeIsSavingState(false);
            const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
            this._updateError(true, errorMessage);
        });
    }

    private _verifyAndSaveServiceEndpoint = (): void => {
        this._changeIsVerifyingState(true);
        this._loadingLabel = Resources.VerifyingSpinnerLabel;
        this._verifyConnection().then((serviceEndpointRequestResult: ServiceEndpointRequestResult) => {
            this._changeIsVerifyingState(false);
            this._loadingLabel = Resources.SavingSpinnerLabel;
            if (serviceEndpointRequestResult.errorMessage) {
                this._updateError(true, serviceEndpointRequestResult.errorMessage, null, serviceEndpointRequestResult.activityId);
            } else {
                this._saveServiceEndpoint();
            }
        }).catch((error) => {
            this._changeIsVerifyingState(false);
            this._loadingLabel = Resources.SavingSpinnerLabel;
            const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
            this._updateError(true, errorMessage);
        });
    }

    private _updateError = (isError: boolean, errorMessage: string, errorStatus?: string, activityId?: string): void => {
        if (isError && !errorStatus) {
            Utils_Accessibility.announce(errorMessage);
        }
        this.setState({
            isError: isError,
            errorMessage: errorMessage,
            activityId: activityId
        });
    }

    private _changeIsVerifyingState(isVerifying: boolean) {
        this._isVerifying = isVerifying;
        this._reloadFooterBar();
    }

    private _changeIsSavingState(isSaving: boolean) {
        this._isSaving = isSaving;
        this._reloadFooterBar();
    }

    private _reloadFooterBar() {
        this._reloadFooter.value = !this._reloadFooter.value;
    }

    private _verifyConnection(): Promise<ServiceEndpointRequestResult> {
        let dataSourceDetails: DataSourceDetails = {
            dataSourceName: TestConnectionDataSource,
            dataSourceUrl: "",
            headers: null,
            resourceUrl: "",
            requestContent: null,
            requestVerb: null,
            parameters: null,
            resultSelector: "",
            initialContextTemplate: ""
        };

        let resultTransformationDetails: ResultTransformationDetails = {
            callbackContextTemplate: "",
            callbackRequiredTemplate: "",
            resultTemplate: ""
        };

        let serviceEndpointRequest: ServiceEndpointRequest = {
            dataSourceDetails: dataSourceDetails,
            resultTransformationDetails: resultTransformationDetails,
            serviceEndpointDetails: this.state.serviceEndpoint
        };

        return this._actionCreator.verifyServiceEndpoint(serviceEndpointRequest, this.state.serviceEndpoint.id);
    }

    private _isUpdateFlow(): boolean {
        return this.props.contentType === EndpointsEditorPanelContentType.UpdateForm;
    }

    private _isAuthSchemeSupported(): boolean {
        if (this.state.serviceEndpointType
            && this.state.serviceEndpoint.authorization.scheme
            && this.state.serviceEndpointType.authenticationSchemes
            && this.state.serviceEndpointType.authenticationSchemes.findIndex((authScheme) => {
                return Utils_String.equals(authScheme.scheme, this.state.serviceEndpoint.authorization.scheme, true);
            }) < 0) {

            return false;
        }

        return true;
    }

    private _actionCreator: EndpointsEditorActionsCreator;
    private _store: EndpointsEditorStore;
    private _verifiableConnection: boolean;
    private _isVerifying: boolean;
    private _isSaving: boolean;
    private _invalidInputsCount: number;
    private _loadingLabel: string;
    private _reloadFooter: ObservableValue<boolean>;
    private _hideFooter: boolean;
}